import { useLazyQuery } from '@apollo/client';
import { useMutation } from './apollo';
import { gql } from '@apollo/client';
import { useCallback, useState } from 'react';
import saveAs from 'file-saver';
import { convertBase64ToFile } from 'utils/base64FileConversion';

const UPLOAD_FILE_MUTATION = gql`
  mutation uploadFile($input: UploadFileInput!) {
    uploadFile(input: $input) {
      commandId
    }
  }
`;

const GET_FILE_QUERY = gql`
  query documentTemplateFile($fileName: String!) {
    documentTemplateFile(fileName: $fileName) {
      fileName
      content
    }
  }
`;

const CHECK_FILE_EXISTENCE_QUERY = gql`
  query documentTemplateFileCheck($fileName: String!) {
    documentTemplateFileCheck(fileName: $fileName) {
      isUploaded
    }
  }
`;

export function useFileUpload({ onStarting = _ => {}, onError = _ => {} } = {}) {
  const [fileName, setFileName] = useState();

  const [uploadFileMutation, { loading: uploading, error, ...rest }] = useMutation(UPLOAD_FILE_MUTATION, {
    onError: err => {
      onError(err);
    }
  });

  const uploadFile = useCallback(
    (file, metadata) => {
      if (!file) {
        return;
      }
      setFileName(file.name);
      onStarting(file);
      uploadFileMutation({ variables: { input: { file, ...metadata } } });
    },
    [uploadFileMutation, onStarting]
  );

  return [uploadFile, { uploading, fileName, error, ...rest }];
}

export function useFileDownload({ onStarting = () => {}, onCompleted = () => {}, onError = () => {}, activeService } = {}) {
  const [fileName, setFileName] = useState();
  const [getFileQuery, { loading: downloading, error, ...rest }] = useLazyQuery(GET_FILE_QUERY, {
    context: { serviceName: activeService?.name },
    onCompleted: result => {
      onCompleted(result.documentTemplateFile.fileName);
      saveAs(convertBase64ToFile(result.documentTemplateFile.content), result.documentTemplateFile.fileName);
    },
    onError: err => {
      onError(err);
    },
    fetchPolicy: 'network-only'
  });

  const downloadFile = useCallback(
    fileName => {
      if (!fileName) {
        return;
      }
      setFileName(fileName);
      onStarting(fileName);
      getFileQuery({ variables: { fileName } });
    },
    [getFileQuery, onStarting]
  );

  return [downloadFile, { downloading, fileName, error, ...rest }];
}

export function useFileCheck({ onCompleted = () => {}, onError = () => {}, activeService } = {}) {
  const [fileName, setFileName] = useState();
  const [checkFileExistenceQuery] = useLazyQuery(CHECK_FILE_EXISTENCE_QUERY, {
    context: { serviceName: activeService?.name },
    onCompleted: result => {
      onCompleted(result.documentTemplateFileCheck?.isUploaded || false);
    },
    onError: err => {
      onError(err);
    },
    fetchPolicy: 'network-only'
  });

  const checkFile = useCallback(
    fileName => {
      if (!fileName) {
        return;
      }
      setFileName(fileName);
      checkFileExistenceQuery({ variables: { fileName } });
    },
    [checkFileExistenceQuery]
  );

  return [checkFile, { fileName }];
}
